import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, SubscriptionLike, Subscription, from } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';
import { map, switchMap, tap } from 'rxjs/operators';
import { BroadcastChannels, BroadcastService } from 'app/utils/broadcastService';
import {
  AppSettingsService,
  AuthenticationService,
  AuthorizationService,
  DEVICE_OS_FAMILY,
  GlobalEventBusService,
  GroupContextService,
  hd,
  IDeviceModel,
  IMCConnection,
  IRequestOptions,
  ITicketSessionChangedStatus,
  McConnectionsService,
  TicketSessionStatus,
  XDeviceService,
} from '@nx-fe/services';
import { GlobalEvents } from 'app/core/global-event-constants';
import { DevicePermissionsMap } from 'app/modules/remote-control/services/device-state-registry.service';
import { HttpService } from '../../../../../../../libs/services/http.service';
import { Activity, ActivityTypeEnum, isDeviceSnapshotActivity } from '../ticket.model';
import { DeviceCaptureActivityTypes } from '../ticket-page.service';

export enum NavbarItems {
  device_details,
  device_configurations,
  device_application,
  device_security,
  device_content,
  device_notes,
}

export enum DeviceSnapshotSaveRequestStatus {
  None, // Nothing pending (also initial state)
  SaveRequested, // A Snapshot Save request was made
  SaveErrored, // There was an error saving the snapshot
}

export enum SnapshotSaveStatus {
  Unsaved,
  Saving,
  Saved,
}

export enum snapshotAction {
  Save,
  Refresh,
}

export interface IDeviceSnapshotStatus {
  action: snapshotAction;
  status?: DeviceSnapshotSaveRequestStatus; // The new status
  activityId: number; // The activity ID the snapshot status change is about
  message?: string; // holds a messages related with the new status (ie: error => the error message)
  deviceId?: string; // the deviceId for the given snapshot
  snapshot?: SnapshotModel;
}

/**
 * State of a snapshot for a specific device.
 */
export interface IDeviceSnapshotMetaData {
  deviceId: string;
  activityId: number;
  saveStatus: SnapshotSaveStatus;
  lastRefreshed: Date;
  currentPage: string;
  isPendingUpdate: boolean;
}

/**
 * State of the SnapshotService as a whole.
 */
export interface ISnapshotServiceState {
  deviceSnapshotsMap: { [deviceId: string]: IDeviceSnapshotMetaData };
  elementInViewMap: { [deviceId: string]: string };
  fetchingSnapshot: boolean;
}

export enum DeviceSnapshotPropertyStatus {
  Normal = 0,
  Warning = 1,
  Error = 2,
  Unknown = 3,
}

export interface SnapshotProperty {
  key: string;
  resourceCaption: string;
  status: DeviceSnapshotPropertyStatus;
  statusMessage: string;
  importance: number;
  value: string;
}

export interface SnapshotSection {
  key?: string;
  resourceCaption?: string;
  isSupported?: boolean;
  sections?: { [sectionId: string]: SnapshotSection };
  properties?: { [propId: string]: SnapshotProperty };
  rows?: Array<{ [rowId: string]: string }>;
}

export interface SnapshotModel {
  activityId: number;
  deviceId: string;
  deviceName?: string;
  kind: string;
  family: string;
  mcConnectionId?: number;
  sections: { [sectionId: string]: SnapshotSection };
  mcDeviceId: string;
  isSaved: boolean;
  rawData: { [itemId: string]: string };
  snapshotDate: Date;
  isExpired: boolean;
}

export interface SnapshotModelSerialised {
  activityId: number;
  deviceId: string;
  deviceName: string;
  details: string;
  snapshotDate: Date;
  family: string;
  kind: string;
  mcConnectionId: number;
  mcDeviceId: string;
}

@Injectable({
  providedIn: 'root',
})
export class SnapshotService implements OnDestroy {
  private _fetchedSnapshotsMap: { [deviceId: string]: IDeviceSnapshotMetaData };
  private _elementInViewMap: { [deviceId: string]: string };
  private lastSnapshot: SnapshotModel; // Store the last retrieved snapshot for saving
  // if multiple devices can snapshot in parallel, this needs to be keyed by deviceId/mcConnectionId

  private readonly ticketSessionChanged: BroadcastService<ITicketSessionChangedStatus> =
    new BroadcastService<ITicketSessionChangedStatus>(BroadcastChannels.onTicketSessionChanged);
  private readonly deviceSnapshotStatusChanged: BroadcastService<IDeviceSnapshotStatus> =
    new BroadcastService<IDeviceSnapshotStatus>(BroadcastChannels.onDeviceSnapshotStatusChanged);
  private readonly deviceSnapshotRequestInProgress: BroadcastService<hd.DeviceSnapshotRequestStatus> =
    new BroadcastService(BroadcastChannels.onDeviceSnapshotRequestInProgress);
  private readonly snapshotViewVisibilityChanged: BroadcastService<void> = new BroadcastService(
    BroadcastChannels.onSnapshotViewVisibilityChanged
  );

  private readonly snapshotFetchingKey = 'fetchDeviceSnapshotLockout';

  private isFetching = new BehaviorSubject<boolean>(false);

  public fetchingStatus$: Observable<boolean>;
  private _fetchingSnapshot = false;
  private permissionSubscription: SubscriptionLike;
  private devicePermissions: DevicePermissionsMap = {};

  private deviceFetchStatusSubscription: Subscription;
  private activityDeletedSub: Subscription;

  public get fetchingSnapshot(): boolean {
    return this._fetchingSnapshot;
  }

  public set fetchingSnapshot(value: boolean) {
    this._fetchingSnapshot = value;
    this.isFetching.next(value);
  }

  public getElementInView(deviceId: string): string {
    // if no element set use default
    if (!this._elementInViewMap[deviceId]) {
      this._elementInViewMap[deviceId] = NavbarItems[NavbarItems.device_details];
    }
    return this._elementInViewMap[deviceId];
  }

  public setElementInView(deviceId: string, elementName: string): void {
    this._elementInViewMap[deviceId] = elementName;
  }

  public clearElementInViewState(deviceId: string) {
    if (deviceId && this._elementInViewMap && this._elementInViewMap[deviceId]) {
      delete this._elementInViewMap[deviceId];
    }
  }

  constructor(
    private http: HttpService,
    private appSettingsService: AppSettingsService,
    private authenticationService: AuthenticationService,
    private authorizationService: AuthorizationService,
    private globalEventBus: GlobalEventBusService,
    // private deviceCheckinService: DeviceCheckinStateService,
    private groupContextService: GroupContextService,
    private xDeviceService: XDeviceService,
    private mcConnectionService: McConnectionsService
  ) {
    this.deviceSnapshotStatusChanged.onMessage = this.onDeviceSnapshotStatusChanged;
    this.ticketSessionChanged.onMessage = this.onTicketSessionChanged;
    this._fetchedSnapshotsMap = {}; // Dictionary of [deviceId : ISnapshotState]
    this._elementInViewMap = {};

    // XTODO:
    // this.deviceCheckinService.deviceCheckinCompleted$.subscribe(resp => {
    //     if (resp) {
    //         this.setIsPendingUpdate(resp.deviceId, true);
    //     }
    // });

    this.fetchingStatus$ = this.isFetching.asObservable();

    this.deviceFetchStatusSubscription = this.fetchingStatus$.subscribe(isFetching => {
      this.broadcastLockout(<hd.DeviceSnapshotRequestStatus>{
        requestName: this.snapshotFetchingKey,
        inProgress: isFetching,
      });
    });

    this.snapshotViewVisibilityChanged.onMessage = () => {
      this.fetchingSnapshot = false;
      this.broadcastLockout(<hd.DeviceSnapshotRequestStatus>{
        requestName: this.snapshotFetchingKey,
        inProgress: false,
      });
    };

    this.activityDeletedSub = this.globalEventBus.register(
      GlobalEvents.activityDeleted,
      (activity: hd.IDeviceCaptureActivity) => {
        this.resetSnapshotSaveStatus(activity);
      }
    );
  }

  ngOnDestroy() {
    if (this.deviceSnapshotStatusChanged) {
      this.deviceSnapshotStatusChanged.close();
    }
    if (this.ticketSessionChanged) {
      this.ticketSessionChanged.close();
    }
    if (this.deviceSnapshotRequestInProgress) {
      this.deviceSnapshotRequestInProgress.close();
    }

    if (this.permissionSubscription) {
      this.permissionSubscription.unsubscribe();
    }
    if (this.snapshotViewVisibilityChanged) {
      this.snapshotViewVisibilityChanged.close();
    }

    if (this.deviceFetchStatusSubscription) {
      this.deviceFetchStatusSubscription.unsubscribe();
    }

    if (this.activityDeletedSub) {
      this.activityDeletedSub.unsubscribe();
    }
  }

  /**
   * Indicates if the snapshot for the specified device can be saved.
   */
  public canSaveSnapshot = (deviceId: string): boolean => {
    const entry = this._fetchedSnapshotsMap[deviceId];
    return entry ? entry.saveStatus === SnapshotSaveStatus.Unsaved : true;
  };

  /**
   * Only used for device-info component
   */
  public setSnapshotLastRefreshed = (deviceId: string, date: Date): void => {
    const entry = this._fetchedSnapshotsMap[deviceId];
    if (entry && date) {
      entry.lastRefreshed = date;
    }
  }

  /**
   * Returns the timestamp when the snapshot was last refreshed for the
   * specified device, or null if there is no snapshot yet.
   */
  public getSnapshotLastRefreshed = (deviceId: string): Date => {
    const entry = this._fetchedSnapshotsMap[deviceId];
    return entry ? entry.lastRefreshed : null;
  };

  /**
   * Returns the snapshot page currently being viewed for the specified device.
   */
  public getCurrentSnapshotPageView = (deviceId: string): string => {
    const entry = this._fetchedSnapshotsMap[deviceId];
    return entry ? entry.currentPage : null;
  };

  /**
   * Resets the snapshot page currently being viewed for the specified device.
   */
  public resetCurrentSnapshotPageView = (deviceId: string): void => {
    const entry = this._fetchedSnapshotsMap[deviceId];
    if (entry) {
      entry.currentPage = null;
    }
  };

  /**
   * Indicates if the snapshot for the specified device has pending updates to
   * be retrieved via a refresh.
   */
  public getIsPendingUpdate = (deviceId: string): boolean => {
    const entry = this._fetchedSnapshotsMap[deviceId];
    return entry ? entry.isPendingUpdate : false;
  };

  /**
   * Sets whether or not the snapshot for the specified device has pending updates
   * available to be retrieved via a refresh.
   */
  public setIsPendingUpdate = (deviceId: string, isPendingUpdate: boolean) => {
    const entry = this._fetchedSnapshotsMap[deviceId];
    if (entry) {
      entry.isPendingUpdate = isPendingUpdate;
    } else {
      console.error('There is no SnapshotState for device "' + deviceId + '"');
    }
  };

  /**
   * Returns the snapshot metadata for the specified device.
   */
  getSnapshotMetaData = (deviceId: string): IDeviceSnapshotMetaData => this._fetchedSnapshotsMap[deviceId];

  /**
   * Assigns the provided snapshot metadata to the indicated device.
   */
  setSnapshotMetaData = (
    deviceId: string,
    activityId: number,
    snapshotDate: Date,
    saveStatus: SnapshotSaveStatus,
    currentPage: string
  ) => {
    this._fetchedSnapshotsMap[deviceId] = {
      deviceId,
      activityId,
      saveStatus,
      lastRefreshed: snapshotDate,
      currentPage,
      isPendingUpdate: this.getIsPendingUpdate(deviceId),
    };
  };

  /**
   * Returns the state of the snapshot service. Used for pop-in/pop-out actions.
   */
  getSnapshotServiceState = (): ISnapshotServiceState => {
    return {
      deviceSnapshotsMap: this._fetchedSnapshotsMap,
      elementInViewMap: this._elementInViewMap,
      fetchingSnapshot: this.fetchingSnapshot,
    };
  };

  /**
   * Sets the state of the snapshot service. Used for pop-in/pop-out actions.
   */
  setSnapshotServiceState = (serviceState: ISnapshotServiceState) => {
    this._fetchedSnapshotsMap = serviceState.deviceSnapshotsMap;
    this._elementInViewMap = serviceState.elementInViewMap;
    this.fetchingSnapshot = serviceState.fetchingSnapshot;
  };

  /**
   * Makes the call to server to create a snapshot for the specified device.
   */
  getDeviceSnapshot = (device: IDeviceModel): Observable<SnapshotModel> => {
    let headers = new HttpHeaders();
    headers = HttpService.addSnapshotDisplayOptionsHeaders(headers);

    const options: IRequestOptions = {
      headers,
    };
    return this.http.post<SnapshotModel>(this.appSettingsService.snapshotServiceUrl, device, options).pipe(
      tap(resp => {
        if (resp && resp.deviceId) {
          this.lastSnapshot = resp;
          if (this.getIsPendingUpdate(resp.deviceId) === true) {
            this.setIsPendingUpdate(resp.deviceId, false);
          }
        }
      })
    );
  };

  /**
   * Broadcasts a message, so the status change can be processed by
   * who has the required information (ticketId and activityId)
   */
  requestSaveDeviceSnapshot = (deviceId: string): void => {
    const snapshotState = this._fetchedSnapshotsMap[deviceId];
    if (snapshotState == null) {
      console.error(`No snapshot has been retrieved for device '${deviceId}'. Cannot request to save.`);
      return;
    }

    // Only unsaved snapshots can be saved
    if (snapshotState.saveStatus === SnapshotSaveStatus.Unsaved) {
      this._fetchedSnapshotsMap[deviceId].saveStatus = SnapshotSaveStatus.Saving;

      // Send the DeviceSnapshotSaveStatus with a Requested status,
      // so this gets saved by the ticketController...
      this.deviceSnapshotStatusChanged.postMessage({
        action: snapshotAction.Save,
        status: DeviceSnapshotSaveRequestStatus.SaveRequested,
        activityId: snapshotState.activityId,
        snapshot: this.lastSnapshot,
        deviceId,
      } as IDeviceSnapshotStatus);
    }
  };

  /**
   * Makes the call to server to save the snapshot.
   */
  saveDeviceSnapshot = (snapshot: SnapshotModel, ticketId: number): Observable<number> => {
    return this.getDeviceInstanceName(snapshot.mcConnectionId).pipe(
      switchMap((deviceInstanceName: string) => {
        const snapshotSerialized: SnapshotModelSerialised = {
          details: JSON.stringify(snapshot.sections),
          deviceId: snapshot.deviceId,
          deviceName: snapshot.deviceName,
          snapshotDate: snapshot.snapshotDate,
          family: snapshot.family,
          kind: snapshot.kind,
          mcConnectionId: snapshot.mcConnectionId,
          mcDeviceId: snapshot.mcDeviceId,
          activityId: snapshot.activityId,
        };

        const activity = {
          activityType: ActivityTypeEnum[ActivityTypeEnum.deviceSnapshot],
          captureType: DeviceCaptureActivityTypes.snapshot,
          mcConnectionName: deviceInstanceName,
          ...snapshotSerialized,
        };

        return this.http
          .post<Activity>(this.appSettingsService.ticketServiceUrl + `${ticketId}/activities`, activity)
          .pipe(
            switchMap((activity: Activity) => {
              return from([activity.activityId]);
            })
          );
      })
    );
  };

  /**
   * Makes the call to the server to retrieve a previously created snapshot.
   */
  getSavedDeviceSnapshot = (activityId: number): Observable<SnapshotModel> => {
    const headers = HttpService.addSnapshotDisplayOptionsHeaders(new HttpHeaders());
    const options: IRequestOptions = {
      headers: headers,
    };

    return this.http.get<Activity>(this.appSettingsService.snapshotServiceUrl + activityId, options).pipe(
      map(activity => {
        if (typeof activity.details === 'object' && Object.keys(activity.details).includes('0')) {
          activity.details = activity.details[0];
        }
        if (isDeviceSnapshotActivity(activity)) {
          // Snapshot
          if (typeof activity.details.data === 'string') {
            activity.details.data = JSON.parse(activity.details.data);
          }

          const result: SnapshotModel = {
            activityId: activity.activityId,
            deviceId: activity.details.deviceId,
            kind: undefined,
            family: activity.details.family,
            mcConnectionId: activity.details.mcConnectionId,
            sections: activity.details.data,
            mcDeviceId: activity.details.mcDeviceId,
            isSaved: true,
            snapshotDate: new Date(activity.details.snapshotDate),
            rawData: undefined,
            isExpired: false,
          };

          return result;
        }
        return undefined;
      })
    );
  };

  /**
   * Generate PDF of Device Snapshot
   */
  exportSnapshot = (activityId: number): Observable<Blob> => {
    const options: IRequestOptions = {
      headers: new HttpHeaders(),
      responseType: 'blob' as 'json',
    };
    return this.http.post<Blob>(this.appSettingsService.snapshotServiceUrl + activityId + '/export', null, options);
  };

  /**
   * Indicates if for the specified device has snapshot permissions.
   */
  hasSnapshotPermissions = (device: IDeviceModel): boolean => {
    if (!device || !device.deviceId) {
      return false;
    }

    if (!!device && !!device.family && !this.isPlatformSupported(device.family)) {
      return false;
    }

    if (this.devicePermissions[device.deviceId]) {
      const perm = this.devicePermissions[device.deviceId].permissions;
      return (
        // eslint-disable-next-line no-bitwise
        (perm & hd.RemoteControlPermissions.RemoteControlViewSystemInfo) ===
        hd.RemoteControlPermissions.RemoteControlViewSystemInfo
      );
    } else {
      // if don't have permissions for current deviceId, insert with 0
      this.devicePermissions[device.deviceId] = {
        deviceId: device.deviceId,
        permissions: 0,
      } as hd.IRemoteControlPermissionsModel;
    }

    this.permissionSubscription = this.xDeviceService.getDevicePermissions(device.deviceId).subscribe(
      (response: hd.IRemoteControlPermissionsModel) => {
        if (response) {
          this.devicePermissions[device.deviceId] = response;
        }
      },
      error => {
        console.error('Failed to get permissions for deviceId = ' + device.deviceId);
        console.error(error);
      }
    );
    return false;
  };

  isPlatformSupported(deviceFamily: string): boolean {
    return [
      DEVICE_OS_FAMILY.Apple,
      DEVICE_OS_FAMILY.AndroidPlus,
      DEVICE_OS_FAMILY.Linux,
      DEVICE_OS_FAMILY.WindowsModernDesktop,
      DEVICE_OS_FAMILY.WindowsDesktop,
      DEVICE_OS_FAMILY.WindowsPhone,
      DEVICE_OS_FAMILY.WindowsCE,
      DEVICE_OS_FAMILY.Printer,
    ].includes(deviceFamily);
  }

  private onDeviceSnapshotStatusChanged = (snapshotStatus: IDeviceSnapshotStatus): void => {
    let newSaveStatus: SnapshotSaveStatus;

    if (snapshotStatus.action === snapshotAction.Save) {
      // Update the snapshot save status based on the result of the save
      switch (snapshotStatus.status) {
        case DeviceSnapshotSaveRequestStatus.None:
          newSaveStatus = SnapshotSaveStatus.Saved;
          break;

        case DeviceSnapshotSaveRequestStatus.SaveErrored:
          newSaveStatus = SnapshotSaveStatus.Unsaved;
          break;

        case DeviceSnapshotSaveRequestStatus.SaveRequested:
          newSaveStatus = SnapshotSaveStatus.Saving;
          break;
      }

      const state = this._fetchedSnapshotsMap[snapshotStatus.deviceId];
      if (state && state.saveStatus) {
        this._fetchedSnapshotsMap[snapshotStatus.deviceId].saveStatus = newSaveStatus;
      }
    }
  };

  private onTicketSessionChanged = (params: ITicketSessionChangedStatus) => {
    // Reset the service state whenever a new ticket is loaded
    if (params.status === TicketSessionStatus.Started) {
      this._fetchedSnapshotsMap = {};
      this.fetchingSnapshot = false;
    }
  };

  private broadcastLockout = (lockoutStatus: hd.DeviceSnapshotRequestStatus) => {
    this.deviceSnapshotRequestInProgress.postMessage(lockoutStatus);
  };

  /**
   * Reset the Snapshot savestatus value upon deletion of snapshot activity.
   */
  private resetSnapshotSaveStatus = (activity: hd.IDeviceCaptureActivity) => {
    const snapshotActivity = activity.childActivities?.find(
      x => x.activityType === 'devicesnapshot'
    ) as hd.ISnapshotActivity;
    if (snapshotActivity) {
      if (
        this._fetchedSnapshotsMap[activity.deviceId] != null &&
        snapshotActivity.activityId === this._fetchedSnapshotsMap[activity.deviceId].activityId
      ) {
        this._fetchedSnapshotsMap[activity.deviceId].saveStatus = SnapshotSaveStatus.Unsaved;
      }
    }
  };

  /** Find the mcInstance Name for device */
  getDeviceInstanceName = (mcId: number): Observable<string> => {
    return this.mcConnectionService.getById(mcId).pipe(map((value: IMCConnection) => value.connectionName));
  };
}
